import React from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import PageMeta from '../components/PageMeta'

const TermsOfServicePage = () => {
  return (
    <div>
      <PageMeta 
        title="Terms of Service | RDS Studio"
        description="Review the terms and conditions governing your use of RDS Studio's website and services."
        canonical="https://rdsstudio.co.za/terms-of-service"
      />
      <Header />
      <main className="main-content">
        <section className="section legal-page">
          <div className="section-inner">
            <h1 className="title title-h2">Terms of Service</h1>
            <p className="description" style={{ marginBottom: '2rem' }}>
              Last updated: December 11, 2025
            </p>

            <div className="legal-content">
              <h2>1. Agreement to Terms</h2>
              <p>
                By accessing and using the website <strong>https://rdsstudio.co.za</strong> and engaging with RDS Studio's services, you agree to be bound by these Terms of Service. If you do not agree to abide by the above, please do not use this service.
              </p>

              <h2>2. Use License</h2>
              <p>
                Permission is granted to temporarily download one copy of the materials (information or software) on the RDS Studio website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
              </p>
              <ul>
                <li>Modifying or copying the materials</li>
                <li>Using the materials for any commercial purpose or for any public display</li>
                <li>Attempting to decompile or reverse engineer any software contained on the website</li>
                <li>Removing any copyright or other proprietary notations from the materials</li>
                <li>Transferring the materials to another person or "mirroring" the materials on any other server</li>
                <li>Using automated tools, bots, or scrapers to collect data without authorization</li>
                <li>Engaging in any conduct that restricts or inhibits anyone's use or enjoyment of the website</li>
              </ul>

              <h2>3. Disclaimer</h2>
              <p>
                The materials on RDS Studio's website are provided on an 'as is' basis. RDS Studio makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
              </p>

              <h2>4. Limitations of Liability</h2>
              <p>
                In no event shall RDS Studio or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on RDS Studio's website, even if RDS Studio or an authorized representative has been notified orally or in writing of the possibility of such damage.
              </p>

              <h2>5. Accuracy of Materials</h2>
              <p>
                The materials appearing on RDS Studio's website could include technical, typographical, or photographic errors. RDS Studio does not warrant that any of the materials on the website are accurate, complete, or current. RDS Studio may make changes to the materials contained on its website at any time without notice.
              </p>

              <h2>6. Materials on Website</h2>
              <p>
                RDS Studio has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by RDS Studio of the site. Use of any such linked website is at the user's own risk.
              </p>

              <h2>7. Modifications to Terms</h2>
              <p>
                RDS Studio may revise these terms of service for the website at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of service.
              </p>

              <h2>8. Governing Law & Jurisdiction</h2>
              <p>
                These terms and conditions are governed by and construed in accordance with the laws of South Africa, and you irrevocably submit to the exclusive jurisdiction of the courts in Centurion, South Africa.
              </p>

              <h2>9. Service Engagement Terms</h2>

              <h3>9.1 Project Scope & Deliverables</h3>
              <ul>
                <li>All project scope, deliverables, timeline, and pricing are outlined in a formal project proposal or statement of work (SOW)</li>
                <li>Changes to scope must be approved in writing and may incur additional fees</li>
                <li>Delays caused by client feedback, content, or approvals will extend project timelines</li>
              </ul>

              <h3>9.2 Payment Terms</h3>
              <ul>
                <li>Payment terms are specified in individual project agreements (typically 50% deposit, 50% on completion)</li>
                <li>Invoices are due within 30 days unless otherwise agreed</li>
                <li>Late payments may incur 1.5% monthly interest and suspension of services</li>
                <li>All fees are exclusive of applicable VAT/taxes unless stated otherwise</li>
                <li>We accept bank transfers, credit cards, and wire transfers as payment methods</li>
              </ul>

              <h3>9.3 Intellectual Property Rights</h3>
              <ul>
                <li>RDS Studio retains ownership of all custom code, designs, and frameworks developed unless explicitly transferred via signed agreement</li>
                <li>Client owns all original content (copy, images, videos) provided to RDS Studio</li>
                <li>Upon full payment, client receives a non-exclusive license to use deliverables for business purposes</li>
                <li>RDS Studio retains the right to use work samples for portfolio and case studies (anonymized or with permission)</li>
                <li>Third-party licenses and open-source software retain their original terms</li>
              </ul>

              <h3>9.4 Support & Maintenance</h3>
              <ul>
                <li>Support terms vary by project type and are outlined in individual agreements</li>
                <li>Bug fixes related to original delivery are typically included for 30 days post-launch</li>
                <li>Ongoing support, hosting, maintenance, and enhancements are available as paid services</li>
                <li>Response times for support requests are outlined in service level agreements</li>
              </ul>

              <h3>9.5 Termination</h3>
              <ul>
                <li>Either party may terminate a project with written notice and 15 days preparation</li>
                <li>Termination fees apply as outlined in the project agreement</li>
                <li>Upon termination, client pays for all completed work and reasonable expenses incurred</li>
                <li>Deliverables in progress will be provided in current state without refund</li>
              </ul>

              <h2>10. Confidentiality</h2>
              <p>
                Both RDS Studio and clients agree to maintain confidentiality of proprietary information shared during the engagement. This includes:
              </p>
              <ul>
                <li>Business strategies and technical implementations</li>
                <li>Client data and customer information</li>
                <li>Pricing, financial, and business data</li>
                <li>Confidentiality obligations expire 2 years after project completion</li>
              </ul>

              <h2>11. User-Generated Content & Submissions</h2>
              <p>
                Any content you submit, post, or display on our Site (including contact form submissions, project briefs, and feedback) grants RDS Studio a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, and distribute such content for operational purposes.
              </p>

              <h2>12. Prohibited Conduct</h2>
              <p>
                You agree not to engage in any of the following prohibited conduct:
              </p>
              <ul>
                <li>Harassing, threatening, or abusing any person or organization</li>
                <li>Submitting false, fraudulent, or misleading information</li>
                <li>Engaging in illegal activities or violating applicable laws</li>
                <li>Transmitting viruses, malware, or harmful code</li>
                <li>Spam or mass unsolicited communications</li>
                <li>Attempting to gain unauthorized access to our systems</li>
                <li>Violating intellectual property rights</li>
              </ul>

              <h2>13. Warranty Disclaimers</h2>
              <ul>
                <li><strong>Website Availability:</strong> The website and services are provided "as-is" without guarantee of uninterrupted access</li>
                <li><strong>Third-Party Services:</strong> We are not responsible for outages in third-party services we integrate with</li>
                <li><strong>Security:</strong> While we implement security measures, we cannot guarantee absolute protection against breaches</li>
                <li><strong>Performance:</strong> Website performance metrics are estimates and not guaranteed</li>
              </ul>

              <h2>14. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless RDS Studio and its officers, directors, employees, and agents from any claims, damages, losses, and expenses (including attorney's fees) arising from:
              </p>
              <ul>
                <li>Your violation of these Terms</li>
                <li>Your use of the Site or services</li>
                <li>Content you provide or submit</li>
                <li>Infringement of intellectual property rights related to your content</li>
              </ul>

              <h2>15. Limitation of Liability</h2>
              <p>
                In no case shall RDS Studio, its directors, officers, or agents be liable for any indirect, incidental, special, consequential, or punitive damages, even if advised of the possibility of such damages.
              </p>

              <h2>16. Website Maintenance & Downtime</h2>
              <p>
                RDS Studio may perform maintenance on the website requiring temporary downtime. We will attempt to provide notice, but do not guarantee service availability.
              </p>

              <h2>17. Export Controls</h2>
              <p>
                You agree to comply with all applicable export control laws and regulations. You will not export or re-export services or products to prohibited countries or entities.
              </p>

              <h2>18. Entire Agreement</h2>
              <p>
                These Terms of Service, along with any applicable privacy policy and project agreements, constitute the entire agreement between you and RDS Studio regarding your use of the Site and services.
              </p>

              <h2>19. Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in effect, and the unenforceable provision will be modified to the minimum extent necessary to make it enforceable.
              </p>

              <h2>20. Contact Information</h2>
              <p>
                If you have any questions regarding these Terms of Service, please contact us:
              </p>
              <ul>
                <li><strong>Email:</strong> <a href="mailto:legal@rdsstudio.co.za">legal@rdsstudio.co.za</a></li>
                <li><strong>Website:</strong> <a href="https://rdsstudio.co.za">https://rdsstudio.co.za</a></li>
                <li><strong>Location:</strong> Centurion, South Africa</li>
              </ul>

              <p style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <strong>Acceptance of Terms:</strong> By using this website and engaging with RDS Studio services, you acknowledge that you have read, understood, and agree to be bound by all terms and conditions outlined in this document.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}

export default TermsOfServicePage
